import { useEffect, useMemo, useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import EmployeeLayout from '../components/EmployeeLayout';
import TodoList from '../../org-admin/components/TodoList';
import '../styles/Employee.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

// The employee's personal to-do list reuses the same rich list the org-admin
// uses — search, active/deleted, per-row status, time logging, edit modal,
// origin filter — scoped to "mine" (to-dos I created or am assigned). What it
// feeds in that's employee-specific:
//   • taskPool       — my still-open tasks, so a new to-do can be linked to one
//   • assigneeFilter — I can only delegate to myself or teammates below my level
//   • taskLinkBase   — origin chips open the employee task route, not org-admin's
//   • defaultAssignee— new to-dos default to me (this is my personal list)
//
// `enableDetailModal` is what makes a row open — without it the comments and
// time logs on a to-do are unreachable here, though both are built and both
// endpoints are ungated. The org-admin list has always passed it; this one
// simply never did.
function EmployeeTodos() {
  const revision = useLiveRefresh();
  const { user } = useAuth();
  const myId = user?._id || user?.id;
  const myLevel = user?.role?.level ?? 0;
  const [taskPool, setTaskPool] = useState([]);

  useEffect(() => {
    // Only open tasks can take a new to-do — the backend rejects linking to a
    // completed one anyway.
    apiClient
      .get('/api/tasks/my-tasks')
      .then((res) => setTaskPool((res.data || []).filter((t) => t.status !== 'completed')))
      .catch(() => {});
  }, [revision]);

  // Mirrors the server's rule (canAssignTodo): myself, or anyone strictly below.
  const assigneeFilter = useMemo(
    () => (u) => u._id === myId || (u.role?.level ?? 0) < myLevel,
    [myId, myLevel]
  );

  return (
    <EmployeeLayout title="My To-Dos" subtitle="Your to-dos — and ones you delegate to your team">
      <div className="employee-page">
        <TodoList
          scope="mine"
          taskLinkBase="/tasks"
          taskPool={taskPool}
          assigneeFilter={assigneeFilter}
          defaultAssignee={myId}
          enableDetailModal
        />
      </div>
    </EmployeeLayout>
  );
}

export default EmployeeTodos;
